'use strict';

/**
 * AepsModule — AEPS (Aadhaar Enabled Payment System) operations.
 *
 * Every call goes through fintech-gateway's wildcard proxy at
 * POST/GET /process/aeps/<path>, which forwards verbatim to paymentSystem's
 * /aeps/<path> (see src/modules/interface/nsdl/aeps/aeps.route.js, mounted
 * at /aeps). Field shapes are paymentSystem's to validate — the gateway
 * doesn't inspect them.
 */
class AepsModule {
  constructor(client) {
    this._c = client;
  }

  _post(path, body = {}) { return this._c.post(`aeps/${path}`, body); }
  _get(path, body = {})  { return this._c.get(`aeps/${path}`, body); }

  // ── Onboarding ───────────────────────────────────────────────────

  /** Initialize this terminal's AEPS onboarding session. No fields. */
  initialize()                    { return this._post('initialize'); }

  /** Kick off merchant onboarding with NSDL. No fields. */
  onboarding()                    { return this._post('onboarding'); }

  /** Request the onboarding OTP. No fields. */
  requestOtp()                    { return this._post('request_otp'); }

  /** @param {{ otp: string, otpreqid: string }} body */
  validateOtp(body)               { return this._post('validate_otp', body); }

  /** @param {{ pan: string, name: string, dob: string }} body */
  verifyPan(body)                 { return this._post('verify_pan', body); }

  /** @param {{ aadhaarno: string }} body */
  updateUid(body)                 { return this._post('update_UID', body); }

  /** @param {{ PidData: string, aadhaarno: string }} body - PidData is biometric capture XML */
  updateBiodetail(body)           { return this._post('updatebiodetail', body); }

  /** Submit the completed onboarding application. No fields. */
  submit()                        { return this._post('submit'); }

  /** Onboarding status of the merchant/agent identity resolved from credentials. */
  statusCheck()                   { return this._get('statusCheck'); }

  /** @param {{ agentId?: string }} [body] - defaults to your terminal's MID when omitted */
  getAgentDetail(body = {})       { return this._post('GetAgentDetail', body); }

  // ── Daily agent authentication ────────────────────────────────────

  /**
   * Required once every 24h before withdrawal/balance/minStatement/purchase.
   * @param {{ AadharNo: string, Latitude: string, Longitude: string,
   *            CustomerName: string, PidData: string, BankName?: string }} body
   */
  reqAuth(body)                   { return this._post('reqAuth', body); }

  /** Read-only — whether today's agent authentication is already valid. No fields. */
  authStatus()                    { return this._get('authStatus'); }

  // ── Transactions ─────────────────────────────────────────────────
  // withdrawal/purchase/balance/minStatement share one field shape:
  //   AadharNo, TxnAmount (withdrawal/purchase only), Latitude, Longitude,
  //   CustomerName, PidData, BankName?

  /** @param {{ AadharNo: string, TxnAmount: number, Latitude: string, Longitude: string,
   *            CustomerName: string, PidData: string, BankName?: string }} body */
  withdrawal(body)                { return this._post('withdrawal', body); }

  /** @param {{ AadharNo: string, Latitude: string, Longitude: string,
   *            CustomerName: string, PidData: string, BankName?: string }} body */
  balance(body)                   { return this._post('balance', body); }

  /** @param {{ AadharNo: string, Latitude: string, Longitude: string,
   *            CustomerName: string, PidData: string, BankName?: string }} body */
  minStatement(body)              { return this._post('minstatement', body); }

  /** Aadhaar Pay (merchant purchase debit).
   * @param {{ AadharNo: string, TxnAmount: number, Latitude: string, Longitude: string,
   *            CustomerName: string, PidData: string, BankName?: string }} body */
  purchase(body)                  { return this._post('purchase', body); }

  // ── Bank / IIN ───────────────────────────────────────────────────

  /** NSDL IIN -> bank name list, for the "Select Bank" dropdown. */
  banks()                         { return this._get('banks'); }
}

module.exports = { AepsModule };
